/**
 * Agent Simulator
 *
 * Simulates the AI agent processing workflow with realistic delays.
 * Generates mock extracted data and clarification questions.
 */

import type {
  ClarificationQuestion,
  CpBand,
  CpCurriculumPlan,
  CpOptionBlock,
  CpSet,
  CpSubject,
  CpSubjectSlot,
  CpTeacher,
  ValidationError,
} from '../types/curriculum_planning';
import { generateId } from './utils/idGenerator';

export interface ExtractedEntities {
  teachers: Partial<CpTeacher>[];
  subjects: Partial<CpSubject>[];
  bands: Partial<CpBand>[];
  sets: Partial<CpSet>[];
  optionBlocks: Partial<CpOptionBlock>[];
  subjectSlots: Partial<CpSubjectSlot>[];
  curriculumPlans: Partial<CpCurriculumPlan>[];
}

export interface AgentProgressCallback {
  onStatusChange: (status: string) => void;
  onMessage: (type: string, content: string, metadata?: Record<string, unknown>) => void;
  onProgress: (step: number, total: number, description: string) => void;
}

/**
 * Delay helper for simulating async operations
 */
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Simulates the AI agent workflow
 */
export class AgentSimulator {
  private callbacks: AgentProgressCallback;

  constructor(callbacks: AgentProgressCallback) {
    this.callbacks = callbacks;
  }

  /**
   * Main processing workflow
   */
  async processFiles(fileNames: string[]): Promise<ExtractedEntities> {
    const { onStatusChange, onMessage, onProgress } = this.callbacks;

    // Step 1: Parsing
    onStatusChange('parsing');
    onMessage('progress', `Parsing ${fileNames.length} uploaded file(s)...`);
    onProgress(1, 6, 'Parsing files');
    await delay(1500);

    onMessage('thinking', 'Identifying document structure and format...');
    await delay(1000);

    // Step 2: Analyzing
    onStatusChange('analyzing');
    onMessage('progress', 'Analyzing document structure...');
    onProgress(2, 6, 'Analyzing structure');
    await delay(1500);

    onMessage('thinking', 'Detecting curriculum planning data patterns...');
    await delay(1200);

    // Step 3: Extracting
    onStatusChange('extracting');
    onMessage('progress', 'Extracting curriculum entities...');
    onProgress(3, 6, 'Extracting entities');
    await delay(2000);

    onMessage('thinking', 'Identifying teachers, subjects, and groupings...');
    await delay(1500);

    // Generate mock extracted data
    const extractedData = this.generateMockExtractedData();

    const counts = {
      teachers: extractedData.teachers.length,
      subjects: extractedData.subjects.length,
      bands: extractedData.bands.length,
      sets: extractedData.sets.length,
      optionBlocks: extractedData.optionBlocks.length,
    };

    onMessage(
      'progress',
      `Extracted: ${counts.teachers} teachers, ${counts.subjects} subjects, ${counts.bands} bands, ${counts.sets} sets, ${counts.optionBlocks} option blocks`,
      { counts },
    );

    // Step 4: Validating
    onStatusChange('validating');
    onMessage('progress', 'Validating extracted data...');
    onProgress(4, 6, 'Validating data');
    await delay(1500);

    onMessage('thinking', 'Checking for duplicates and inconsistencies...');
    await delay(1000);

    onProgress(5, 6, 'Processing complete');

    return extractedData;
  }

  /**
   * Generates mock extracted entities from "uploaded files"
   */
  generateMockExtractedData(): ExtractedEntities {
    const now = Date.now();

    return {
      teachers: [
        {
          id: generateId('teacher'),
          firstName: 'Sarah',
          lastName: 'Williams',
          initials: 'SW',
          email: 's.williams@school.edu',
          employeeCode: 'EMP101',
          fte: 1.0,
          maxPeriodsPerWeek: 22,
          maxPeriodsPerCycle: 22,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: generateId('teacher'),
          firstName: 'Michael',
          lastName: 'Roberts',
          initials: 'MR',
          email: 'm.roberts@school.edu',
          employeeCode: 'EMP102',
          fte: 0.8,
          maxPeriodsPerWeek: 18,
          maxPeriodsPerCycle: 18,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: generateId('teacher'),
          firstName: 'Emma',
          lastName: 'Thompson',
          initials: 'ET',
          email: 'e.thompson@school.edu',
          employeeCode: 'EMP103',
          fte: 1.0,
          maxPeriodsPerWeek: 22,
          maxPeriodsPerCycle: 22,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        },
      ],
      subjects: [
        {
          id: generateId('subj'),
          name: 'Psychology',
          code: 'PSY',
          categoryId: 'cat-elective',
          requiresSpecialistRoom: false,
          maxClassSize: 28,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: generateId('subj'),
          name: 'Sociology',
          code: 'SOC',
          categoryId: 'cat-elective',
          requiresSpecialistRoom: false,
          maxClassSize: 28,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        },
      ],
      bands: [
        {
          id: generateId('band'),
          name: 'Express Band',
          code: 'Y7E',
          yearGroupId: 'yg-7',
          academicYearId: 'ay-2024-2025',
          displayOrder: 0,
          createdAt: now,
          updatedAt: now,
        },
      ],
      sets: [
        {
          id: generateId('set'),
          name: 'Y10 Science Set 1',
          code: 'Y10S1',
          yearGroupId: 'yg-10',
          academicYearId: 'ay-2024-2025',
          pupilCount: 28,
          capacity: 30,
          displayOrder: 1,
          createdAt: now,
          updatedAt: now,
        },
        {
          id: generateId('set'),
          name: 'Y10 Science Set 2',
          code: 'Y10S2',
          yearGroupId: 'yg-10',
          academicYearId: 'ay-2024-2025',
          pupilCount: 26,
          capacity: 30,
          displayOrder: 2,
          createdAt: now,
          updatedAt: now,
        },
      ],
      optionBlocks: [
        {
          id: generateId('ob'),
          name: 'Option Block D',
          code: 'Y10D',
          yearGroupId: 'yg-10',
          academicYearId: 'ay-2024-2025',
          periodsPerCycle: 4,
          minSubjectsToChoose: 1,
          maxSubjectsToChoose: 1,
          displayOrder: 4,
          createdAt: now,
          updatedAt: now,
        },
      ],
      subjectSlots: [],
      curriculumPlans: [],
    };
  }

  /**
   * Generates clarification questions based on extracted data
   */
  generateClarifications(): ClarificationQuestion[] {
    return [
      {
        id: 'clarify-1',
        type: 'single-choice',
        question:
          'The initials "SW" appear in both the uploaded file and existing data. Are "S. Williams" and "Sarah Williams" the same person?',
        options: [
          { value: 'same', label: 'Yes, same person - merge records' },
          { value: 'different', label: 'No, different people - create new record' },
        ],
        entityType: 'teacher',
        entityId: 'teacher-sw',
        priority: 'high',
        context: 'Found potential duplicate teacher record',
      },
      {
        id: 'clarify-2',
        type: 'single-choice',
        question: 'Should "Psychology" be categorized as an Elective or Foundation subject?',
        options: [
          { value: 'elective', label: 'Elective (optional for KS4)' },
          { value: 'foundation', label: 'Foundation (part of core curriculum)' },
        ],
        entityType: 'subject',
        priority: 'medium',
        context: 'New subject requires categorization',
      },
      {
        id: 'clarify-3',
        type: 'confirm',
        question:
          'The uploaded file contains a new Option Block D for Year 10. Should this be added to the existing option blocks?',
        options: [
          { value: 'yes', label: 'Yes, add as Option Block D' },
          { value: 'no', label: 'No, skip this entry' },
        ],
        entityType: 'optionBlock',
        priority: 'medium',
      },
    ];
  }

  /**
   * Generates validation errors for review
   */
  generateValidationErrors(): ValidationError[] {
    return [
      {
        id: 'val-1',
        entityType: 'teacher',
        field: 'email',
        message: 'Email format may be invalid: "m.roberts@school" (missing .edu)',
        severity: 'warning',
      },
      {
        id: 'val-2',
        entityType: 'set',
        field: 'capacity',
        message: 'Set capacity (30) exceeds typical class size limit',
        severity: 'warning',
      },
    ];
  }

  /**
   * Generates a completion report
   */
  generateReport(extractedCounts: Record<string, number>, savedCounts: Record<string, number>): string {
    return `# Import Complete

## Summary
The curriculum data import has been completed successfully.

## Entities Imported

| Entity Type | Extracted | Saved |
|------------|-----------|-------|
| Teachers | ${extractedCounts.teachers || 0} | ${savedCounts.teachers || 0} |
| Subjects | ${extractedCounts.subjects || 0} | ${savedCounts.subjects || 0} |
| Bands | ${extractedCounts.bands || 0} | ${savedCounts.bands || 0} |
| Sets | ${extractedCounts.sets || 0} | ${savedCounts.sets || 0} |
| Option Blocks | ${extractedCounts.optionBlocks || 0} | ${savedCounts.optionBlocks || 0} |

## Notes
- All clarification questions were resolved
- Data has been validated and saved to the system
- You can view and edit the imported data in the respective tabs

---
*Import completed at ${new Date().toLocaleString()}*
`;
  }
}
